import * as React from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import omit from 'lodash/omit';
import PropTypes from 'prop-types';

import Retracked from 'js/app/retracked';

import { StyleSheet, css } from '@coursera/coursera-ui';

import Alert from 'bundles/authentication-common/components/Alert';
import Form from 'bundles/authentication-common/components/Form';
import LinkButton from 'bundles/authentication-common/components/LinkButton';
import Submit from 'bundles/authentication-common/components/Submit';
import * as actions from 'bundles/authentication-common/components/actions';
import EmailInput from 'bundles/authentication-common/components/inputs/EmailInput';
import PasswordInput from 'bundles/authentication-common/components/inputs/PasswordInput';
import TwoFactorAuthInput from 'bundles/authentication-common/components/inputs/TwoFactorAuthInput';
import {
  ARKOSE_LOGIN_KEY,
  LOGIN_REDESIGN_RECAPTCHA_ID,
  RECAPTCHA_ACTIONS,
} from 'bundles/authentication-common/constants/botManager';
import { generalErrorMessage } from 'bundles/authentication-common/constants/errors';
import useBotManager from 'bundles/authentication-common/hooks/useBotManager';
import type { EventData, LoginSharedProps, LoginValues } from 'bundles/authentication-common/types';
import { HANDLED_ERRORS, NEEDS_2FA_ERRORS, errorMessageFactory } from 'bundles/authentication-common/utils/loginUtils';
import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { INTERACTION_EVENTS_MAP } from 'bundles/expression-of-interest/constants';
import { useEoiEventing } from 'bundles/expression-of-interest/utils/eventingUtils';
import { trackAuthAttempts } from 'bundles/user-account/utils/authTrackingUtils';

import _t from 'i18n!nls/authentication';

type Context = {
  _eventData: EventData;
};

type Props = LoginSharedProps & {
  extraFormContent?: ReactElement;
  toFocus?: boolean;
  showSubmitButton?: boolean;
  afterSubmit?: () => void;
  beforeSubmit?: () => void;
  trackingProductId: string;
};

const TrackedLinkButton = withSingleTracked({ type: 'BUTTON' })(LinkButton);
const TrackedSubmit = withSingleTracked({ type: 'BUTTON' })(Submit);

const styles = StyleSheet.create({
  container: {
    display: 'grid',
    gridGap: '12px',
    gridTemplateColumns: '1fr',
  },
  experiment: {
    display: 'flex',
    'justify-content': 'space-between',
  },
  item: {
    ':nth-child(1)': {
      '-ms-grid-row': '1',
    },
    ':nth-child(2)': {
      '-ms-grid-row': '2',
    },
    ':nth-child(3)': {
      '-ms-grid-row': '3',
    },
    ':nth-child(4)': {
      '-ms-grid-row': '4',
    },
    ':nth-child(5)': {
      '-ms-grid-row': '5',
    },
  },
  reCAPTCHA: {
    display: 'flex',
    'justify-content': 'center',
    ':nth-of-type(n) .grecaptcha-badge': { display: 'none !important' },
  },
  submit: {
    marginTop: '12px',
    width: '100%',
  },
  styleConflictInputFix: {
    ':nth-of-type(n) input': {
      border: `0px !important`,
      height: 'unset',
      marginBottom: '0px !important',
      padding: '12px 16px',
      lineHeight: '22px',
      fontSize: '16px',
      outline: 'none',
      boxShadow: 'none !important',
      color: 'unset',
    },
    ':nth-of-type(n) input:focus:required:invalid:focus': { color: 'unset' },
    ':nth-of-type(n) input::placeholder': { color: 'var(--cds-color-grey-500)', opacity: 1 },
    /* Internet Explorer 10-11 */
    ':nth-of-type(n) input:-ms-input-placeholder': { color: 'var(--cds-color-grey-500)', opacity: 1 },
    /* Edge */
    ':nth-of-type(n) input:-webkit-input-placeholder': { color: 'var(--cds-color-grey-500)', opacity: 1 },
  },
});

const LoginForm = (props: Props, { _eventData }: Context) => {
  const {
    toFocus,
    defaultEmail,
    extraFormContent,
    showSubmitButton = true,
    trackingProductId,
    onForgotPassword,
    afterSubmit,
    onSuccess,
    beforeSubmit,
    onError,
  } = props;

  const [has2FA, setHas2FA] = useState<boolean>(false);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [error, setError] = useState<string | ReactNode>('');

  const input = useRef<HTMLInputElement | null>(null);

  const { trackInteractEoi } = useEoiEventing(trackingProductId);
  const { v2TrackingName, v3EventKey } = INTERACTION_EVENTS_MAP.loginFormSubmitButton;

  const {
    isRetrievingTokenRef,
    getTokenData: getBotManagerTokenData,
    reset: resetBotManager,
  } = useBotManager({
    arkoseBotManager: {
      publicKey: ARKOSE_LOGIN_KEY,
    },
    reCAPTCHA: {
      container: LOGIN_REDESIGN_RECAPTCHA_ID,
      options: { action: RECAPTCHA_ACTIONS.login },
    },
  });

  useEffect(() => {
    if (toFocus) {
      input.current?.focus();
    }
  }, [_eventData, toFocus]);

  const handleError = (response: $TSFixMe) => {
    resetBotManager();

    const { errorCode: code } = response?.responseJSON || {};

    if (HANDLED_ERRORS.includes(code)) {
      if (code === 'invalidCredential') {
        // @ts-expect-error TS7016 TODO: code has type any
        setError(errorMessageFactory()[code]());
        input.current?.focus();
      } else {
        // @ts-expect-error TS7016 TODO: code has type any
        setError(errorMessageFactory()[code]);
      }

      setHas2FA(NEEDS_2FA_ERRORS.includes(code));
      setIsRunning(false);
    } else {
      onError(response);
    }
    afterSubmit?.();
  };

  const login = async (values: LoginValues) => {
    // Exit early if a token retrieval is in progress to prevent multiple function calls.
    if (isRetrievingTokenRef?.current) return false;

    if (!values.email || !values.password) {
      return setError(errorMessageFactory().invalidRequest);
    }

    let tokenData;
    try {
      tokenData = await getBotManagerTokenData();
    } catch {
      return setError(generalErrorMessage());
    }

    setIsRunning(true);
    beforeSubmit?.();

    const payload = {
      ...omit(values, 'one-time-password'),
      ...tokenData,
      ...(values['one-time-password'] && { mfa: values['one-time-password'] }),
    };

    trackAuthAttempts(_eventData, 'login');

    try {
      const response = await actions.login(payload);
      trackInteractEoi({
        eoiInteraction: v3EventKey,
      });

      return onSuccess(response?.redirectTo as string);
    } catch (e) {
      return handleError(e);
    }
  };

  const onInvalid = () => {
    setError(undefined);
  };

  const onFocus = (trackingName: string) => {
    Retracked.trackComponent(_eventData, {}, trackingName, 'login_text_input_focus');
  };

  return (
    <Form
      {...css('rc-LoginForm', styles.container)}
      name="login"
      // @ts-expect-error FIXME result of fixing typing on coursera-ui aphrodite exports in PR#45351
      onSubmit={login}
      onInvalid={onInvalid}
      id="eoi-modal-login-form"
    >
      {error && !isRunning && (
        <Alert {...css(styles.item)} type="error">
          {error}
        </Alert>
      )}
      <EmailInput
        {...css(styles.styleConflictInputFix, styles.item)}
        autoFocus
        data-e2e="login-email-input"
        defaultValue={defaultEmail}
        ref={input}
        onFocus={() => onFocus('email')}
      />
      <PasswordInput
        {...css(styles.styleConflictInputFix, styles.item)}
        data-e2e="login-password-input"
        hint={
          <TrackedLinkButton onClick={onForgotPassword} trackingName="login_form_forgot_password_link">
            {_t('Forgot password?')}
          </TrackedLinkButton>
        }
        placeholder={_t('Enter your password')}
        onFocus={() => onFocus('password')}
        autoComplete="current-password"
      />
      {extraFormContent}
      {has2FA && (
        <TwoFactorAuthInput
          {...css(styles.styleConflictInputFix, styles.item)}
          autoFocus
          data-e2e="verification-code-field"
        />
      )}
      <div {...css(styles.reCAPTCHA)} id={LOGIN_REDESIGN_RECAPTCHA_ID} data-size="invisible" />
      {showSubmitButton && (
        <TrackedSubmit
          {...css(styles.submit, styles.item, styles.submit)}
          data-e2e="login-form-submit-button"
          disabled={isRunning}
          trackingName={v2TrackingName}
        >
          {_t('Login')}
        </TrackedSubmit>
      )}
    </Form>
  );
};

LoginForm.contextTypes = {
  _eventData: PropTypes.object.isRequired,
};

export default Retracked.createTrackedContainer<Props>(() => ({
  namespace: {
    page: 'login',
  },
}))(LoginForm);
